export function formatDateKey(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

export function buildDateKey(year, month, day) {
  return formatDateKey(new Date(year, month, day));
}

export function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function parseDate(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const [year, month, day] = String(value).split('T')[0].split('-').map(Number);
  if (![year, month, day].every(Number.isFinite)) return null;

  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function addDays(date, amount) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

export function formatTime(value, locales = []) {
  if (!value) return '';

  const [hours = '0', minutes = '0'] = String(value).split(':');
  const date = new Date();
  date.setHours(Number(hours), Number(minutes), 0, 0);

  return date.toLocaleTimeString(locales, { hour: 'numeric', minute: '2-digit' });
}

export function formatEventKind(kind) {
  switch (kind) {
    case 'NO_SCHOOL':
      return 'No School';
    case 'EARLY_RELEASE':
      return 'Early Release';
    case 'LATE_START':
      return 'Late Start';
    case 'CUSTOM':
    default:
      return 'School Event';
  }
}

export function parseEventNotes(notes) {
  const [title, ...rest] = String(notes || '').split('\n');
  return {
    title: title || 'Schedule change',
    detail: rest.join('\n').trim(),
  };
}

export function buildCalendarDays(monthDate) {
  const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const daysInMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
  const mondayIndex = (firstDay.getDay() + 6) % 7;
  const totalCells = Math.ceil((mondayIndex + daysInMonth) / 7) * 7;

  return Array.from({ length: totalCells }, (_, index) => {
    const offset = index - mondayIndex;
    const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), offset + 1);

    return {
      date,
      key: formatDateKey(date),
      inMonth: date.getMonth() === monthDate.getMonth(),
      day: date.getDate(),
    };
  });
}

export function buildEventDateMap(events, { includeRange = true } = {}) {
  const map = new Map();

  events.forEach((event) => {
    const start = parseDate(event.start_date);
    if (!start) return;

    const end = includeRange
      ? parseDate(event.end_date || event.start_date) || start
      : start;

    for (let current = new Date(start); current <= end; current = addDays(current, 1)) {
      const key = formatDateKey(current);
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(event);
    }
  });

  return map;
}

export function buildSortedEventGroups(events, options = {}) {
  return [...buildEventDateMap(events, options).entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([dateKey, dateEvents]) => ({
      dateKey,
      date: parseDate(dateKey),
      events: [...dateEvents].sort((left, right) => String(left.start_time || '').localeCompare(String(right.start_time || ''))),
    }));
}
